import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { getContract, CONTRACT_ADDRESS } from '../utils/contract';
import { useNavigate } from 'react-router-dom';

function AuditLog({ provider, account }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [selectedRecordForGrant, setSelectedRecordForGrant] = useState(null);

  const navigate = useNavigate();

  const loadEvents = async () => {
    if (!provider) {
      setError('Please connect your wallet to view audit logs');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const contract = getContract(provider);
      
      // For BlockDAG, we need to use getLogs differently
      // Try to use provider's getLogs method directly
      const currentBlock = await provider.getBlockNumber();
      const fromBlock = Math.max(0, currentBlock - 5000); // Reduced range for BlockDAG

      // Create filters manually for BlockDAG compatibility
      const recordUploadedTopic = ethers.id('RecordUploaded(address,string,uint256)');
      const accessUpdatedTopic = ethers.id('AccessUpdated(address,address,bool,uint256)');
      const accessAttemptTopic = ethers.id('AccessAttempt(address,address,bool,uint256)');

      const [uploadedEvents, updatedEvents, attemptEvents] = await Promise.all([
        // Use provider.getLogs directly with BlockDAG compatible parameters
        provider.getLogs({
          address: CONTRACT_ADDRESS,
          topics: [recordUploadedTopic],
          fromBlock: fromBlock,
          toBlock: 'latest'
        }).catch(() => []), // Fallback to empty array if fails
        provider.getLogs({
          address: CONTRACT_ADDRESS,
          topics: [accessUpdatedTopic],
          fromBlock: fromBlock,
          toBlock: 'latest'
        }).catch(() => []),
        provider.getLogs({
          address: CONTRACT_ADDRESS,
          topics: [accessAttemptTopic],
          fromBlock: fromBlock,
          toBlock: 'latest'
        }).catch(() => [])
      ]);

      const formattedEvents = [];

      // Parse raw log data
      uploadedEvents.forEach(event => {
        try {
          const parsed = contract.interface.parseLog(event);
          formattedEvents.push({
            type: 'RecordUploaded',
            patient: parsed.args.patient,
            cid: parsed.args.cid,
            timestamp: Number(parsed.args.timestamp),
            blockNumber: event.blockNumber,
            txHash: event.transactionHash
          });
        } catch (e) {
          console.warn('Failed to parse RecordUploaded event:', e);
        }
      });

      updatedEvents.forEach(event => {
        try {
          const parsed = contract.interface.parseLog(event);
          formattedEvents.push({
            type: 'AccessUpdated',
            patient: parsed.args.patient,
            grantee: parsed.args.grantee,
            granted: parsed.args.granted,
            timestamp: Number(parsed.args.timestamp),
            blockNumber: event.blockNumber,
            txHash: event.transactionHash
          });
        } catch (e) {
          console.warn('Failed to parse AccessUpdated event:', e);
        }
      });

      attemptEvents.forEach(event => {
        try {
          const parsed = contract.interface.parseLog(event);
          formattedEvents.push({
            type: 'AccessAttempt',
            accessor: parsed.args.accessor,
            patient: parsed.args.patient,
            success: parsed.args.success,
            timestamp: Number(parsed.args.timestamp),
            blockNumber: event.blockNumber,
            txHash: event.transactionHash
          });
        } catch (e) {
          console.warn('Failed to parse AccessAttempt event:', e);
        }
      });

      formattedEvents.sort((a, b) => b.timestamp - a.timestamp);
      setEvents(formattedEvents);
    } catch (err) {
      console.error('Failed to load events:', err);
      if (err.message.includes('eth_getLogs')) {
        setError('BlockDAG does not support event filtering. Showing recent transactions only.');
        // Fallback: try to get recent transactions instead
        loadRecentTransactions();
      } else {
        setError(`Failed to load audit logs: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadRecentTransactions = async () => {
    try {
      // Fallback method: get recent transactions for the contract address
      const contract = getContract(provider);
      const currentBlock = await provider.getBlockNumber();
      const fromBlock = Math.max(0, currentBlock - 1000);
      
      // Get all logs without topic filtering (BlockDAG limitation)
      const logs = await provider.getLogs({
        address: CONTRACT_ADDRESS,
        fromBlock: fromBlock,
        toBlock: 'latest'
      }).catch(() => []);

      const formattedEvents = [];
      logs.forEach(event => {
        try {
          const parsed = contract.interface.parseLog(event);
          if (parsed.name === 'RecordUploaded') {
            formattedEvents.push({
              type: 'RecordUploaded',
              patient: parsed.args.patient,
              cid: parsed.args.cid,
              timestamp: Number(parsed.args.timestamp),
              blockNumber: event.blockNumber,
              txHash: event.transactionHash
            });
          } else if (parsed.name === 'AccessUpdated') {
            formattedEvents.push({
              type: 'AccessUpdated',
              patient: parsed.args.patient,
              grantee: parsed.args.grantee,
              granted: parsed.args.granted,
              timestamp: Number(parsed.args.timestamp),
              blockNumber: event.blockNumber,
              txHash: event.transactionHash
            });
          } else if (parsed.name === 'AccessAttempt') {
            formattedEvents.push({
              type: 'AccessAttempt',
              accessor: parsed.args.accessor,
              patient: parsed.args.patient,
              success: parsed.args.success,
              timestamp: Number(parsed.args.timestamp),
              blockNumber: event.blockNumber,
              txHash: event.transactionHash
            });
          }
        } catch (e) {
          console.warn('Failed to parse event:', e);
        }
      });

      formattedEvents.sort((a, b) => b.timestamp - a.timestamp);
      setEvents(formattedEvents.slice(0, 50)); // Limit to 50 most recent
    } catch (err) {
      console.error('Fallback also failed:', err);
      setError('Unable to load audit logs. BlockDAG has limited event log support.');
    }
  };

  useEffect(() => {
    if (provider) {
      loadEvents();
    }
  }, [provider]);

  useEffect(() => {
    if (!provider) return;

    const contract = getContract(provider);

    const handleRecordUploaded = (patient, cid, timestamp, event) => {
      setEvents(prev => [{
        type: 'RecordUploaded',
        patient,
        cid,
        timestamp: Number(timestamp),
        blockNumber: event.blockNumber,
        txHash: event.transactionHash
      }, ...prev]);
    };

    const handleAccessUpdated = (patient, grantee, granted, timestamp, event) => {
      setEvents(prev => [{
        type: 'AccessUpdated',
        patient,
        grantee,
        granted,
        timestamp: Number(timestamp),
        blockNumber: event.blockNumber,
        txHash: event.transactionHash
      }, ...prev]);
    };

    const handleAccessAttempt = (accessor, patient, success, timestamp, event) => {
      setEvents(prev => [{
        type: 'AccessAttempt',
        accessor,
        patient,
        success,
        timestamp: Number(timestamp),
        blockNumber: event.blockNumber,
        txHash: event.transactionHash
      }, ...prev]);
    };

    contract.on('RecordUploaded', handleRecordUploaded);
    contract.on('AccessUpdated', handleAccessUpdated);
    contract.on('AccessAttempt', handleAccessAttempt);

    return () => {
      contract.off('RecordUploaded', handleRecordUploaded);
      contract.off('AccessUpdated', handleAccessUpdated);
      contract.off('AccessAttempt', handleAccessAttempt);
    };
  }, [provider]);

  const filteredEvents = filter === 'all'
    ? events
    : events.filter(e => e.type === filter);

  const getEventIcon = (type) => {
    switch (type) {
      case 'RecordUploaded': return '📤';
      case 'AccessUpdated': return '🔐';
      case 'AccessAttempt': return '👁️';
      default: return '📋';
    }
  };

  const getEventColor = (event) => {
    if (event.type === 'RecordUploaded') return '#10b981';
    if (event.type === 'AccessUpdated') return event.granted ? '#3b82f6' : '#ef4444';
    if (event.type === 'AccessAttempt') return event.success ? '#10b981' : '#ef4444';
    return '#6b7280';
  };

  const formatAddress = (address) => `${address.slice(0, 6)}...${address.slice(-4)}`;

  const handleGrantAccess = (record) => {
    setSelectedRecordForGrant(record);
    setShowGrantModal(true);
  };

  const handleGrantModalClose = () => {
    setShowGrantModal(false);
    setSelectedRecordForGrant(null);
  };

  const handleGoToUpload = () => {
    handleGrantModalClose();
    navigate('/upload');
  };

  return (
    <div>
      <div className="card">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700' }}>Audit Log</h2>
          <button onClick={loadEvents} disabled={loading || !provider} className="btn btn-secondary">
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        {!provider ? (
          <div className="alert alert-info">
            Please connect your wallet to view audit logs.
          </div>
        ) : (
          <>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                Filter Events
              </label>
              <select value={filter} onChange={(e) => setFilter(e.target.value)} className="input">
                <option value="all">All Events</option>
                <option value="RecordUploaded">Record Uploads</option>
                <option value="AccessUpdated">Access Updates</option>
                <option value="AccessAttempt">Access Attempts</option>
              </select>
            </div>

            {loading && <div className="spinner"></div>}

            {!loading && filteredEvents.length === 0 && (
              <div className="alert alert-info">
                No events found. Events will appear here as actions occur on the blockchain.
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {filteredEvents.map((event, index) => (
                <div key={`${event.txHash}-${index}`} style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '16px',
                  borderLeft: `4px solid ${getEventColor(event)}`
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'start',
                    marginBottom: '12px',
                    flexWrap: 'wrap',
                    gap: '8px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '20px' }}>{getEventIcon(event.type)}</span>
                      <strong style={{ fontSize: '16px' }}>{event.type}</strong>
                    </div>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                      {new Date(event.timestamp * 1000).toLocaleString()}
                    </span>
                  </div>

                  {event.type === 'RecordUploaded' && (
                    <div style={{ fontSize: '14px', color: '#374151' }}>
                      <p style={{ margin: '4px 0' }}>
                        <strong>Patient:</strong> {formatAddress(event.patient)}
                      </p>
                      <p style={{ margin: '4px 0', wordBreak: 'break-all' }}>
                        <strong>CID:</strong> {event.cid}
                      </p>

                      {/* Grant Access Button */}
                      {account && account.toLowerCase() === event.patient.toLowerCase() && (
                        <div style={{ marginTop: '12px' }}>
                          <button
                            onClick={() => handleGrantAccess(event)}
                            className="btn btn-primary"
                            style={{ fontSize: '12px', padding: '6px 12px' }}
                          >
                            Grant Access
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {event.type === 'AccessUpdated' && (
                    <div style={{ fontSize: '14px', color: '#374151' }}>
                      <p><strong>Patient:</strong> {formatAddress(event.patient)}</p>
                      <p><strong>Grantee:</strong> {formatAddress(event.grantee)}</p>
                      <p><strong>Action:</strong>{' '}
                        <span style={{
                          color: event.granted ? '#10b981' : '#ef4444',
                          fontWeight: '600'
                        }}>
                          {event.granted ? 'Access Granted ✓' : 'Access Revoked ✗'}
                        </span>
                      </p>
                    </div>
                  )}

                  {event.type === 'AccessAttempt' && (
                    <div style={{ fontSize: '14px', color: '#374151' }}>
                      <p><strong>Accessor:</strong> {formatAddress(event.accessor)}</p>
                      <p><strong>Patient:</strong> {formatAddress(event.patient)}</p>
                      <p><strong>Result:</strong>{' '}
                        <span style={{
                          color: event.success ? '#10b981' : '#ef4444',
                          fontWeight: '600'
                        }}>
                          {event.success ? 'Authorized ✓' : 'Denied ✗'}
                        </span>
                      </p>
                    </div>
                  )}

                  <div style={{
                    marginTop: '12px',
                    paddingTop: '12px',
                    borderTop: '1px solid #e5e7eb',
                    fontSize: '12px',
                    color: '#6b7280',
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '8px'
                  }}>
                    <span>Block: {event.blockNumber}</span>
                    <a
                      href={`https://bdagscan.com/tx/${event.txHash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#667eea', textDecoration: 'none' }}
                    >
                      View Transaction →
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Grant Access Modal */}
      {showGrantModal && selectedRecordForGrant && (
        <div className="modal-overlay" onClick={handleGrantModalClose}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: '20px', fontSize: '22px', fontWeight: '700' }}>
              Grant Access to Medical Record
            </h3>

            <div style={{
              background: '#fef3c7',
              border: '1px solid #fbbf24',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#92400e', fontSize: '16px' }}>
                🔑 AES Key Required
              </h4>
              <p style={{ color: '#92400e', lineHeight: '1.5' }}>
                To grant access, please go to the Upload Page where the AES key was generated.
              </p>
            </div>

            <div style={{
              background: '#f3f4f6',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <h5 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600' }}>
                Record Details:
              </h5>
              <p><strong>Patient:</strong> {formatAddress(selectedRecordForGrant.patient)}</p>
              <p style={{ wordBreak: 'break-all' }}>
                <strong>CID:</strong> {selectedRecordForGrant.cid}
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={handleGoToUpload} className="btn btn-primary" style={{ flex: 1 }}>
                Go to Upload Page
              </button>
              <button onClick={handleGrantModalClose} className="btn btn-secondary" style={{ flex: 1 }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="card" style={{ marginTop: '24px' }}>
        <h3 style={{ marginBottom: '16px', fontSize: '20px', fontWeight: '600' }}>
          About Audit Logs
        </h3>
        <p style={{ fontSize: '14px', color: '#374151', lineHeight: '1.6' }}>
          All actions in MedVault are permanently recorded on the BlockDAG blockchain, ensuring transparency and accountability.
        </p>
        <ul style={{ fontSize: '14px', color: '#374151', lineHeight: '1.8', paddingLeft: '20px' }}>
          <li><strong>Record Uploaded:</strong> When a patient uploads a new medical record.</li>
          <li><strong>Access Updated:</strong> When access is granted or revoked.</li>
          <li><strong>Access Attempt:</strong> Every time someone tries to access a record.</li>
        </ul>
      </div>
    </div>
  );
}

export default AuditLog;
